import React, { useState } from 'react';
import { useAlert } from '../context/AlertContext';
import CountyDropdown from './CountyDropdown';
import AgencyDropdown from './AgencyDropdown';
import FacilityDropdown from './FacilityDropdown';
import { COUNTRIES } from '../constants/states';

const StandaloneDetentionForm = ({ onSubmit, loading }) => {
  const { showSuccess, showError } = useAlert();

  // Get current month in YYYY-MM format for default
  const getCurrentMonth = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  };

  const [formData, setFormData] = useState({
    // Basic Demographics (required for standalone records)
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    sex: '',
    countryOfCitizenship: '',
    arrestingAgency: '',

    // Detention Record Fields
    reportMonth: getCurrentMonth(), // Auto-populate to current month
    facilityName: '',
    facilityType: 'COUNTY_JAIL',
    county: '',
    caseNumber: '',
    fbiNumber: '',
    
    // County Jail Data
    iceDetainerReceived: false,
    accompaniedByI200: false,
    accompaniedByI205: false,
    detainerLodged: false,
    detainerCancelledByIce: false,
    releasedIceTookCustody: false,
    iceFailedPickup: false,
    releasedDueToTimeout: false,
    highestOffenseClassification: '',
    releasedAfter48hrViolentCrime: false,
    releasedBeforeIceDetainer: false,
    jemDetainerBeforeIceMatch: false,
    transportedToIceOffice: false,
    housingCostReimbursement: false,
    iceReimbursementRate: '',
    bedDaysIncurred: '',
    isJuvenile: false,
    actualHousingCost: '',
    reimbursementDifference: '',
    releasedHousingCostsUpdates: false, // New checkbox for showing reimbursement field

    // Release tracking fields
    bookOutDate: '',
    releasedMonth: '',

    // FDOC Data
    fdocConfirmedAlienWithDetainer: false,
    fdocConfirmedAlienWithoutDetainer: false,
    fdocIceDetainerRemoved: false,
    fdocReleasedToIceCustody: false,
    fdocScheduledRelease30Days: false,
    fdocReleasedIceRefused: false
  });

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Helper function to handle title case input changes for names
  const handleTitleCaseInputChange = (e) => {
    const { name, value } = e.target;
    const titleCaseValue = toTitleCase(value);
    const syntheticEvent = {
      target: {
        name: name,
        value: titleCaseValue,
        type: 'text'
      }
    };
    handleInputChange(syntheticEvent);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Pre-fill book out date when "Released / Housing Costs Updated" is checked
    if (name === 'releasedHousingCostsUpdates' && checked) {
      const today = new Date();
      const bookOutDate = today.toISOString().split('T')[0]; // YYYY-MM-DD format
      const releasedMonth = today.toISOString().slice(0, 7); // YYYY-MM format

      setFormData(prev => ({
        ...prev,
        bookOutDate: bookOutDate,
        releasedMonth: releasedMonth
      }));
    }

    // Clear book out date and released month when "Released / Housing Costs Updated" is unchecked
    if (name === 'releasedHousingCostsUpdates' && !checked) {
      setFormData(prev => ({
        ...prev,
        bookOutDate: '',
        releasedMonth: ''
      }));
    }

    // Auto-populate released month when book out date changes
    if (name === 'bookOutDate' && value) {
      const releasedMonth = value.slice(0, 7); // Extract YYYY-MM from YYYY-MM-DD
      setFormData(prev => ({
        ...prev,
        releasedMonth: releasedMonth
      }));
    }

    // Clear released month when book out date is cleared
    if (name === 'bookOutDate' && !value) {
      setFormData(prev => ({
        ...prev,
        releasedMonth: ''
      }));
    }

    // Clear book out date and released month when "Released / Housing Costs Updated" is unchecked
    if (name === 'releasedHousingCostsUpdates' && !checked) {
      setFormData(prev => ({
        ...prev,
        bookOutDate: '',
        releasedMonth: ''
      }));
    }

    // Auto-calculate housing costs when bed days change
    if (name === 'bedDaysIncurred') {
      const bedDays = value === '' || value === null || value === undefined ? 0 : parseInt(value) || 0;
      const dailyRate = formData.isJuvenile ? 23.30 : 80.86;
      const actualHousingCost = bedDays * dailyRate;

      if (bedDays === 0) {
        // No reimbursement calculations for 0 bed days
        setFormData(prev => ({
          ...prev,
          iceReimbursementRate: '0.00',
          actualHousingCost: '0.00',
          reimbursementDifference: '0.00'
        }));
      } else {
        // Normal calculation for 1+ bed days
        const iceReimbursement = 50.00; // Flat fee per person
        const reimbursementDifference = actualHousingCost - iceReimbursement;

        setFormData(prev => ({
          ...prev,
          iceReimbursementRate: iceReimbursement.toFixed(2),
          actualHousingCost: actualHousingCost.toFixed(2),
          reimbursementDifference: reimbursementDifference.toFixed(2)
        }));
      }
    }

    // Note: housingCostReimbursement is now a boolean checkbox, no calculation needed

    // Auto-calculate when juvenile status changes
    if (name === 'isJuvenile') {
      const bedDays = parseInt(formData.bedDaysIncurred) || 0;
      const dailyRate = checked ? 23.30 : 80.86; // Juvenile: $23.30, Adult: $80.86
      const iceReimbursement = 50.00; // Flat fee per person

      if (bedDays >= 0) {
        const actualHousingCost = bedDays * dailyRate;

        if (bedDays === 0) {
          // No reimbursement for 0 bed days
          setFormData(prev => ({
            ...prev,
            iceReimbursementRate: '0.00',
            actualHousingCost: actualHousingCost.toFixed(2),
            reimbursementDifference: '0.00'
          }));
        } else {
          // Normal calculation for 1+ bed days
          const reimbursementDifference = actualHousingCost - iceReimbursement;

          setFormData(prev => ({
            ...prev,
            iceReimbursementRate: iceReimbursement.toFixed(2),
            actualHousingCost: actualHousingCost.toFixed(2),
            reimbursementDifference: reimbursementDifference.toFixed(2)
          }));
        }
      }
    }

    // Note: housingCostReimbursement is now a boolean checkbox, no additional calculation needed
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.reportMonth || !formData.facilityName || !formData.facilityType) {
      showError('Please fill in all required detention fields (Report Month, Facility Name, and Facility Type)');
      return;
    }

    if (!formData.firstName || !formData.lastName || !formData.dateOfBirth || !formData.sex || !formData.countryOfCitizenship) {
      showError('Please fill in all required demographic fields (First Name, Last Name, Date of Birth, Sex, and Country of Citizenship)');
      return;
    }

    try {
      // Include demographic data in submission
      const submissionData = {
        ...formData,
        immigrantId: null, // This is a standalone record
        // Add demographic fields for backend processing
        demographicData: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          dateOfBirth: formData.dateOfBirth,
          sex: formData.sex,
          countryOfCitizenship: formData.countryOfCitizenship,
          arrestingAgency: formData.arrestingAgency
        }
      };

      await onSubmit(submissionData);
      showSuccess('Standalone detention record created successfully');
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        sex: '',
        countryOfCitizenship: '',
        arrestingAgency: '',
        reportMonth: '',
        facilityName: '',
        facilityType: 'COUNTY_JAIL',
        county: '',
        caseNumber: '',
        fbiNumber: '',
        iceDetainerReceived: false,
        accompaniedByI200: false,
        accompaniedByI205: false,
        detainerLodged: false,
        detainerCancelledByIce: false,
        releasedIceTookCustody: false,
        iceFailedPickup: false,
        releasedDueToTimeout: false,
        highestOffenseClassification: '',
        releasedAfter48hrViolentCrime: false,
        releasedBeforeIceDetainer: false,
        jemDetainerBeforeIceMatch: false,
        transportedToIceOffice: false,
        housingCostReimbursement: '',
        iceReimbursementRate: '',
        bedDaysIncurred: '',
        isJuvenile: false,
        actualHousingCost: '',
        reimbursementDifference: '',

        // Release tracking fields
        bookOutDate: '',
        releasedMonth: '',

        fdocConfirmedAlienWithDetainer: false,
        fdocConfirmedAlienWithoutDetainer: false,
        fdocIceDetainerRemoved: false,
        fdocReleasedToIceCustody: false,
        fdocScheduledRelease30Days: false,
        fdocReleasedIceRefused: false
      });
    } catch (error) {
      showError(error.message || 'Failed to create standalone detention record');
    }
  };

  const offenseClassifications = [
    'Capital Felony',
    'Life Felony',
    '1st Degree Felony',
    '2nd Degree Felony',
    '3rd Degree Felony',
    '1st Degree Misdemeanor',
    '2nd Degree Misdemeanor',
    'Noncriminal Violation'
  ];

  const sexOptions = [
    'Male',
    'Female'
  ];

  return (
    <form onSubmit={handleSubmit} className="detentionForm">
      {/* Standalone Demographic Information */}
      <div className="formSection">
        <h3>Demographic Information</h3>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>First Name *</strong></label>
            <input
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleTitleCaseInputChange}
              className="modern-input"
              required
            />
          </div>

          <div className="formGroup">
            <label><strong>Last Name *</strong></label>
            <input
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleTitleCaseInputChange}
              className="modern-input"
              required
            />
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Date of Birth *</strong></label>
            <input
              type="date"
              name="dateOfBirth"
              value={formData.dateOfBirth}
              onChange={handleInputChange}
              className="modern-input"
              required
            />
          </div>

          <div className="formGroup">
            <label><strong>Sex *</strong></label>
            <select
              name="sex"
              value={formData.sex}
              onChange={handleInputChange}
              className="modern-input"
              required
            >
              <option value="">Select Sex</option>
              {sexOptions.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Country of Citizenship *</strong></label>
            <select
              name="countryOfCitizenship"
              value={formData.countryOfCitizenship}
              onChange={handleInputChange}
              className="modern-input"
              required
            >
              <option value="">Select Country</option>
              {COUNTRIES.map(country => (
                <option key={country.value} value={country.label}>
                  {country.label}
                </option>
              ))}
            </select>
          </div>

          <div className="formGroup">
            <label><strong>Arresting Agency</strong></label>
            <AgencyDropdown
              name="arrestingAgency"
              value={formData.arrestingAgency}
              onChange={handleInputChange}
              className="modern-input"
            />
          </div>
        </div>
      </div>

      {/* Basic Information */}
      <div className="formSection">
        <h3>Basic Information</h3>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Report Month *</strong></label>
            <input
              type="month"
              name="reportMonth"
              value={formData.reportMonth}
              onChange={handleInputChange}
              className="modern-input"
              required
            />
          </div>

          <div className="formGroup">
            <label><strong>Facility Type *</strong></label>
            <select
              name="facilityType"
              value={formData.facilityType}
              onChange={handleInputChange}
              className="modern-input"
              required
            >
              <option value="COUNTY_JAIL">County Jail</option>
              <option value="FDOC">Florida Department of Corrections</option>
            </select>
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Facility Name *</strong></label>
            <FacilityDropdown
              name="facilityName"
              value={formData.facilityName}
              onChange={handleInputChange}
              className="modern-input"
              required
            />
          </div>

          <div className="formGroup">
            <label><strong>County</strong></label>
            <CountyDropdown
              name="county"
              value={formData.county || ''}
              onChange={handleInputChange}
              className="modern-input"
            />
          </div>
        </div>

        <div className="formRow">
          <div className="formGroup">
            <label><strong>Case/Booking Number</strong></label>
            <input
              type="text"
              name="caseNumber"
              value={formData.caseNumber}
              onChange={handleInputChange}
              className="modern-input"
              placeholder="Internal case or booking number"
            />
          </div>

          <div className="formGroup">
            <label><strong>FBI Number</strong></label>
            <input
              type="text"
              name="fbiNumber"
              value={formData.fbiNumber}
              onChange={handleInputChange}
              className="modern-input"
              placeholder="FBI identification number"
            />
          </div>
        </div>
      </div>

      {/* County Jail Data Section */}
      {formData.facilityType === 'COUNTY_JAIL' && (
        <div className="formSection">
          <h3>County Jail Data Collection</h3>

          {/* Item 1: ICE Detainer Forms */}
          <div className="formSubsection">
            <h4>1. ICE Detainer Forms (I-247)</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="iceDetainerReceived"
                  checked={formData.iceDetainerReceived}
                  onChange={handleInputChange}
                />
                ICE Detainer Received (I-247)
              </label>

              {formData.iceDetainerReceived && (
                <div className="subCheckboxes">
                  <label className="checkboxLabel">
                    <input
                      type="checkbox"
                      name="accompaniedByI200"
                      checked={formData.accompaniedByI200}
                      onChange={handleInputChange}
                    />
                    Accompanied by I-200 (Probable Cause Arrest Warrant)
                  </label>

                  <label className="checkboxLabel">
                    <input
                      type="checkbox"
                      name="accompaniedByI205"
                      checked={formData.accompaniedByI205}
                      onChange={handleInputChange}
                    />
                    Accompanied by I-205 (Warrant of Removal)
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Item 2: Detainer Status */}
          <div className="formSubsection">
            <h4>2. Detainer Status</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="detainerLodged"
                  checked={formData.detainerLodged}
                  onChange={handleInputChange}
                />
                Hold Placed
              </label>

              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="detainerCancelledByIce"
                  checked={formData.detainerCancelledByIce}
                  onChange={handleInputChange}
                />
                Hold Cancelled by ICE
              </label>

              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedIceTookCustody"
                  checked={formData.releasedIceTookCustody}
                  onChange={handleInputChange}
                />
                ICE Took Custody of Inmate
              </label>
            </div>
          </div>

          {/* Item 3: 48-Hour ICE Custody Period */}
          <div className="formSubsection">
            <h4>3. 48-Hour ICE Custody Period</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="iceFailedPickup"
                  checked={formData.iceFailedPickup}
                  onChange={handleInputChange}
                />
                ICE Failed to Take Custody Within 48 Hours
              </label>

              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedDueToTimeout"
                  checked={formData.releasedDueToTimeout}
                  onChange={handleInputChange}
                />
                Released Due to 48-Hour Timeout
              </label>
            </div>
          </div>
          {/* Item 4: Highest State Law Offense Classification */}
          <div className="formSubsection">
            <h4>4. Highest State Law Offense Classification</h4>
            <div className="formGroup">
              <select
                name="highestOffenseClassification"
                value={formData.highestOffenseClassification}
                onChange={handleInputChange}
                className="modern-input"
              >
                <option value="">Select offense classification</option>
                {offenseClassifications.map(classification => (
                  <option key={classification} value={classification}>
                    {classification}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Items 5-8: Additional Data */}
          <div className="formSubsection">
            <h4>5-8. Additional Data</h4>
            <div className="checkboxGroup">
              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedAfter48hrViolentCrime"
                  checked={formData.releasedAfter48hrViolentCrime}
                  onChange={handleInputChange}
                />
                Released After 48hr + Charged with Violent Crime
              </label>

              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="releasedBeforeIceDetainer"
                  checked={formData.releasedBeforeIceDetainer}
                  onChange={handleInputChange}
                />
                Released Before ICE Detainer Received
              </label>

              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="jemDetainerBeforeIceMatch"
                  checked={formData.jemDetainerBeforeIceMatch}
                  onChange={handleInputChange}
                />
                Jail Enforcement Model Detainer Before ICE Match
              </label>

              <label className="checkboxLabel">
                <input
                  type="checkbox"
                  name="transportedToIceOffice"
                  checked={formData.transportedToIceOffice}
                  onChange={handleInputChange}
                />
                Transported to ICE Office by Detention
              </label>
            </div>
          </div>

          {/* Item 9: Housing Cost Reimbursement Data */}
          <div className="formSubsection">
            <h4>9. Housing Cost Reimbursement Data</h4>

            <div className="formRow">
              <div className="formGroup">
                <label><strong>Bed Days Incurred *</strong></label>
                <input
                  type="number"
                  name="bedDaysIncurred"
                  value={formData.bedDaysIncurred}
                  onChange={handleInputChange}
                  className="modern-input"
                  min="0"
                  placeholder="Enter number of bed days"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>Enter bed days to auto-calculate costs</small>
              </div>

              <div className="formGroup" style={{ display: 'flex', alignItems: 'center', paddingTop: '20px' }}>
                <label className="checkboxLabel">
                  <input
                    type="checkbox"
                    name="isJuvenile"
                    checked={formData.isJuvenile}
                    onChange={handleInputChange}
                  />
                  <strong>Juvenile (Under 18)</strong>
                </label>
              </div>
            </div>

            <div className="formRow">
              <div className="formGroup">
                <label>
                  <input
                    type="checkbox"
                    name="releasedHousingCostsUpdates"
                    checked={formData.releasedHousingCostsUpdates}
                    onChange={handleInputChange}
                    style={{ marginRight: '8px' }}
                  />
                  <strong>Released / Housing Costs Updated</strong>
                </label>
              </div>
            </div>

            {formData.releasedHousingCostsUpdates && (
              <>
                <div className="formRow">
                  <div className="formGroup">
                    <label>
                      <input
                        type="checkbox"
                        name="housingCostReimbursement"
                        checked={formData.housingCostReimbursement}
                        onChange={handleInputChange}
                        style={{ marginRight: '8px' }}
                      />
                      <strong>Housing Cost Reimbursement Received</strong>
                    </label>
                  </div>
                </div>

                <div className="formRow">
                  <div className="formGroup">
                    <label><strong>Book Out Date</strong></label>
                    <input
                      type="date"
                      name="bookOutDate"
                      value={formData.bookOutDate}
                      onChange={handleInputChange}
                      className="modern-input"
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Date when inmate was released
                    </small>
                  </div>

                  <div className="formGroup">
                    <label><strong>Released Month</strong></label>
                    <input
                      type="month"
                      name="releasedMonth"
                      value={formData.releasedMonth}
                      readOnly
                      className="modern-input"
                      style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <small style={{ color: '#666', fontSize: '12px' }}>
                      Auto-calculated from Book Out Date
                    </small>
                  </div>
                </div>
              </>
            )}

            <div className="formRow">
              <div className="formGroup">
                <label><strong>ICE Reimbursement Rate ($)</strong></label>
                <input
                  type="number"
                  name="iceReimbursementRate"
                  value={formData.iceReimbursementRate}
                  onChange={handleInputChange}
                  className="modern-input"
                  step="0.01"
                  placeholder="0.00"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>Auto-calculated: $50.00 flat fee (if bed days > 0), but can be manually adjusted</small>
              </div>
            </div>

            <div className="formRow">
              <div className="formGroup">
                <label><strong>Actual Housing Cost ($)</strong></label>
                <input
                  type="number"
                  name="actualHousingCost"
                  value={formData.actualHousingCost}
                  onChange={handleInputChange}
                  className="modern-input"
                  step="0.01"
                  placeholder="0.00"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>Auto-calculated: Bed days × {formData.isJuvenile ? '$23.30' : '$80.86'}, but can be manually adjusted</small>
              </div>

              <div className="formGroup">
                <label><strong>Reimbursement Difference ($)</strong></label>
                <input
                  type="number"
                  name="reimbursementDifference"
                  value={formData.reimbursementDifference}
                  onChange={handleInputChange}
                  className="modern-input"
                  step="0.01"
                  placeholder="0.00"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>Auto-calculated: Actual cost - ICE reimbursement, but can be manually adjusted</small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FDOC Data Section */}
      {formData.facilityType === 'FDOC' && (
        <div className="formSection">
          <h3>Florida Department of Corrections Data</h3>

          <div className="checkboxGroup">
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocConfirmedAlienWithDetainer"
                checked={formData.fdocConfirmedAlienWithDetainer}
                onChange={handleInputChange}
              />
              1. Confirmed Criminal Alien with ICE Detainer
            </label>

            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocConfirmedAlienWithoutDetainer"
                checked={formData.fdocConfirmedAlienWithoutDetainer}
                onChange={handleInputChange}
              />
              2. Confirmed Criminal Alien without ICE Detainer
            </label>

            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocIceDetainerRemoved"
                checked={formData.fdocIceDetainerRemoved}
                onChange={handleInputChange}
              />
              3. ICE Detainer Removed by ICE
            </label>

            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocReleasedToIceCustody"
                checked={formData.fdocReleasedToIceCustody}
                onChange={handleInputChange}
              />
              4. Released to ICE Custody
            </label>

            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocScheduledRelease30Days"
                checked={formData.fdocScheduledRelease30Days}
                onChange={handleInputChange}
              />
              5. Scheduled Release Within 30 Days
            </label>

            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="fdocReleasedIceRefused"
                checked={formData.fdocReleasedIceRefused}
                onChange={handleInputChange}
              />
              6. Released to Community (ICE Refused Custody)
            </label>
          </div>
        </div>
      )}

      {/* Submit Button */}
      <div className="formActions">
        <button
          type="submit"
          className="modern-btn primary"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create Standalone Detention Record'}
        </button>
      </div>
    </form>
  );
};

export default StandaloneDetentionForm;
